import * as React from 'react';
import * as UI from 'core/ui';
import { PaymentMethodDTO, Res } from 'core/api';
import { Currency } from 'core/utils';
import { Button, Col, Cols, ErrorBlock, FormComponent } from 'core/ui';
import { Binding, Result } from 'core/infrastructure';
import classnames from 'classnames';
import * as Payment from '../PrepaymentStatus';

type Props<T> = {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: Payment.Status;
	pendingAmount: number;
	methods: PaymentMethodDTO[];
	isRefund: boolean;
}

function isValidType(data: any): data is Payment.Status {
	return Object.keys(data).every(key => data[key] !== undefined);
}

type State = Payment.Status

// La idea de sacarlo en un control es para poder reutilizarlo, tanto en pedidos como en albaranes.
// Así mismo, como hay que ir actualizando los valores del cambio, de la cantidad a pagar o el entregado
// vamos gestionando a mano el estado de los componentes para que no se nos vaya el foco con el KeyPad.
// Antes llegamos a tener una versión con this.form.updateModel && this.form.remount(...), pero con el remount
// perdíamos el foco.
export class PrepaymentComponent<T> extends React.Component<Props<T>, State> implements FormComponent {

	private txtAmountToPay = React.createRef<UI.PriceBox<State>>();
	private txtPaidAmount = React.createRef<UI.PriceBox<State>>();

	constructor(props: Props<T>) {
		super(props);

		if (!isValidType(this.props.boundValue))
			throw new Error('PrepaymentComponent espera un boundValue del tipo adecuado');

		this.state = { ...this.props.boundValue, Method: props.methods[0] || null };
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ ErrorMessage: errors.join('\n') });
	}

	getValue(): Result<Payment.Status> {
		return Result.ok(this.state);
	}

	getAmountToPay = (): number => {
		const amounToProcesResult = this.txtAmountToPay.current!.getValue();
		return !amounToProcesResult.ok ? 0 : amounToProcesResult.value;
	};

	getPaidAmount = (): number => {
		const paidAmountResult = this.txtPaidAmount.current!.getValue();
		return !paidAmountResult.ok ? 0 : paidAmountResult.value;
	};

	render() {

		const refreshPaidAmount = () => this.txtPaidAmount.current?.setValue(this.state.PaidAmount);

		const refreshAmounts = () => {
			this.txtPaidAmount.current?.setValue(this.state.PaidAmount);
			this.txtAmountToPay.current?.setValue(this.state.AmountToPay);
		};

		const { PriceBox, ComboBox } = UI.controlsFor<State>();
		const onCompleteAmountToPay = async () => this.setState(await Payment.completeAmountToPay(this.state, this.props.pendingAmount), refreshAmounts);
		const onCompletePaidAmount = async () => this.setState(await Payment.completePaidAmount(this.state, this.getAmountToPay()), refreshAmounts);
		const onAmountToPayUpdated = async () => {
			const amountToPay = this.getAmountToPay();
			const mustSyncPaidAmount = this.state.AmountToPay === this.state.PaidAmount && amountToPay !== this.state.AmountToPay;
			this.setState(await Payment.updateChangeAmount(this.state, amountToPay, mustSyncPaidAmount ? amountToPay : this.state.PaidAmount), () => mustSyncPaidAmount && refreshPaidAmount());
		};
		const onPaidAmountUpdated = async () => this.setState(await Payment.updateChangeAmount(this.state, this.state.AmountToPay, this.getPaidAmount()));


		const onMethodChanged = async (values: PaymentMethodDTO[]) => this.setState(await Payment.selectedMethod(this.state, values[0]));

		return <Cols>
			<Col width='2ex' className='text-muted text-26 m-l-md'>
				<i className='fa fa-money' />
			</Col>
			<Col width='*' className='p-l'>
				<p className='text-26 text-muted p-b-lg'>
					{Res.Web.SET_DOCUMENT_PREPAYMENT_NET_AMOUNT_LBL()} <span className='font-bold'>{UI.Format.asMoney(this.props.pendingAmount)}</span>
				</p>

				{/* Como nuestros cols no son responsive (no están pensados para ello), juego con dos labels el del <p> y el interno a los cols, mostrando u ocultando en función del viewport */}
				<p className='text-16 hidden-md hidden-lg'>
					{this.props.isRefund ? Res.Web.SET_DOCUMENT_PREPAYMENT_AMOUNT_TO_REFUND_LBL(Currency.SYMBOL) : Res.Web.SET_DOCUMENT_PREPAYMENT_PREPAY_AMOUNT_LBL(Currency.SYMBOL)}
				</p>
				<Cols className='m-b'>
					<Col className='text-16 m-t-xs m-r-sm hidden-xs hidden-sm' width='*'>{this.props.isRefund ? Res.Web.SET_DOCUMENT_PREPAYMENT_AMOUNT_TO_REFUND_LBL(Currency.SYMBOL) : Res.Web.SET_DOCUMENT_PREPAYMENT_PREPAY_AMOUNT_LBL(Currency.SYMBOL)}</Col>
					<Col className='m-r-xs'>
						<Button onClick={onCompleteAmountToPay} ><i className='fa fa-arrow-right text-info' /></Button>
					</Col>
					<Col width='170px'>
						<PriceBox ref={this.txtAmountToPay} linkTo={x => x.AmountToPay} boundValue={this.state.AmountToPay} onValueChange={onAmountToPayUpdated} />
					</Col>
				</Cols>

				<p className='text-16 hidden-md hidden-lg'>
					{Res.Web.SET_DOCUMENT_PREPAYMENT_PAYMENT_METHOD_LBL()}
				</p>
				<Cols>
					<Col className='text-16 m-t-xs m-r-sm hidden-xs hidden-sm' width='*'>{Res.Web.SET_DOCUMENT_PREPAYMENT_PAYMENT_METHOD_LBL()}</Col>
					<Col width='170px'>
						<ComboBox boundValue={this.state.Method} linkTo={x => x.Method} options={this.props.methods} onValueChanged={onMethodChanged} required />
					</Col>
				</Cols>

				<p className='text-16 hidden-md hidden-lg'>
					{this.props.isRefund ? Res.Web.SET_DOCUMENT_PREPAYMENT_REFUND_AMOUNT_LBL(Currency.SYMBOL) : Res.Web.SET_DOCUMENT_PREPAYMENT_PAID_AMOUNT_LBL(Currency.SYMBOL)}
				</p>
				<Cols>
					<Col className='text-16 m-t-xs m-r-sm hidden-xs hidden-sm' width='*'>{this.props.isRefund ? Res.Web.SET_DOCUMENT_PREPAYMENT_REFUND_AMOUNT_LBL(Currency.SYMBOL) : Res.Web.SET_DOCUMENT_PREPAYMENT_PAID_AMOUNT_LBL(Currency.SYMBOL)}</Col>
					<Col className='m-r-xs'>
						<Button onClick={onCompletePaidAmount} ><i className='fa fa-arrow-right text-info' /></Button>
					</Col>
					<Col width='170px'>
						<PriceBox ref={this.txtPaidAmount} linkTo={x => x.PaidAmount} boundValue={this.state.PaidAmount} onValueChange={onPaidAmountUpdated} />
					</Col>
				</Cols>

				<p className='text-16 hidden-md hidden-lg'>
					{Res.Web.SET_DOCUMENT_PREPAYMENT_CHANGE_AMOUNT_LBL(Currency.SYMBOL)}
				</p>
				<Cols>
					<Col className='text-16 m-t-xs m-r-sm hidden-xs hidden-sm' width='*'>{Res.Web.SET_DOCUMENT_PREPAYMENT_CHANGE_AMOUNT_LBL(Currency.SYMBOL)}</Col>
					<Col width='170px' className='text-16 text-right'>{UI.Format.asMoney(this.state.ChangeAmount)}</Col>
				</Cols>

				<p className={classnames({ 'has-error': this.state.ErrorMessage, 'text-center': true, 'p-t-md': true })}>
					<ErrorBlock msg={this.state.ErrorMessage} />
				</p>
			</Col>
		</Cols>;
	}
}

export type PrepaymentComponentCtor<T> = new (props: Props<T>) => React.Component<Props<T>, Payment.Status>;
